import useMediaQuery from "../../utils/hooks/use-media-query";
import { splitPercentages } from "./financial-analysis-presentation";
import "./financial-analysis-modal.scss";
import MobileModalFrame from "../common-components/mobile-modal-frame";
import { useTranslation as useInterfaceTranslation } from "react-i18next";
import React from "react";
import { Modal } from "react-bootstrap";
import { useTranslation } from "react-i18next";
import { PricingResult } from "../../utils/supplier-pricing-engine";
import { PBtn, fmt, fmt0 } from "./ui";

const COLORS = { material: "#00173d", bend: "#b7791f", cut: "#2f80ed", units: "#1e8e3e", transport: "#8a94a6" };

export default function FinancialAnalysisModal({ show, onHide, result }: { show: boolean; onHide: () => void; result: PricingResult }) {
  const { t: uiT } = useInterfaceTranslation("interface");
  const { t, i18n } = useTranslation(["supplierPricing"]);
  const mobile = useMediaQuery("(max-width: 991.98px)");
  const money = (value: number) => mobile ? fmt(value, 2) : fmt0(value);
  const { totals, byDiameter } = result;
  const processing = totals.bend + totals.cut + totals.units;
  const parts = [
    { key: "material", label: t("order.material", { tons: totals.weightTon.toFixed(2) }), value: totals.material },
    { key: "bend", label: t("order.bend"), value: totals.bend },
    { key: "cut", label: t("order.cut"), value: totals.cut },
    { key: "units", label: t("order.units"), value: totals.units },
    { key: "transport", label: t("order.transport"), value: totals.transport },
  ].filter((p) => p.value > 0);
  const partsSum = parts.reduce((a, p) => a + p.value, 0) || 1;
  const percentages = splitPercentages(parts.map(part => part.value));

  return (
    <Modal show={show} onHide={onHide} centered size="xl" dialogClassName="eb-pr eb-pr-modal eb-pr-financial">
<MobileModalFrame onClose={onHide}>
      <div className="eb-pr-modal-body" dir={mobile ? i18n.dir() : undefined}>
        <h2 className="eb-pr-modal-title">{t("analysis.title")}</h2>
        <div className="eb-pr-kpis">
          <div className="eb-pr-kpi">
            <span>{t("analysis.subtotal")}</span>
            <b className="eb-pr-atomic" dir={mobile ? "ltr" : undefined}>{money(totals.subtotal)} {uiT("NIS")} </b>
          </div>
          <div className="eb-pr-kpi">
            <span>{t("analysis.avgPerTon")}</span>
            <b className="eb-pr-atomic" dir={mobile ? "ltr" : undefined}>{totals.weightTon ? money((totals.material + processing) / totals.weightTon) : "—"} {uiT("NIS")} </b>
            <small className="eb-pr-atomic" dir={mobile ? "ltr" : undefined}>{totals.weightTon.toFixed(3)} {t("units.ton")}</small>
          </div>
          <div className="eb-pr-kpi">
            <span>{t("analysis.processing")}</span>
            <b className="eb-pr-atomic" dir={mobile ? "ltr" : undefined}>{money(processing)} {uiT("NIS")} </b>
          </div>
          <div className="eb-pr-kpi">
            <span>{t("analysis.transport")}</span>
            <b className="eb-pr-atomic" dir={mobile ? "ltr" : undefined}>{money(totals.transport)} {uiT("NIS")} </b>
          </div>
        </div>

        <div>
          <b>{t("analysis.split")}</b>
          <div className="eb-pr-stack" style={{ marginTop: 8 }}>
            {parts.map((p) => (
              <div key={p.key} title={p.label} style={{ width: `${(p.value / partsSum) * 100}%`, background: (COLORS as any)[p.key] }} />
            ))}
          </div>
          <div className="eb-pr-legend" style={{ marginTop: 8 }}>
            {parts.map((p, index) => (
              <span key={p.key}>
                <i style={{ background: (COLORS as any)[p.key] }} />
                {p.label} · {mobile ? percentages[index] : Math.round((p.value / partsSum) * 100)}%
              </span>
            ))}
          </div>
        </div>

        <div>
          <b>{t("analysis.byDiameter")}</b>
          {mobile ? <div className="eb-pr-diameter-cards">
            {byDiameter.map(row => <article className="eb-pr-diameter-card" key={String(row.diameter)}>
              <h3>{t("diameter.col")} <bdi className="eb-pr-atomic">{row.diameter ?? "—"}</bdi></h3>
              <dl>{[
                [t("analysis.weight"), `${fmt(row.weightTon, 3)} ${t("units.ton")}`],
                [t("order.material", { tons: "" }).replace(/\(.*\)/, "").trim(), `${money(row.material)} ${uiT("NIS")}`],
                [t("order.bend"), `${money(row.bend)} ${uiT("NIS")}`],
                [t("order.cut"), `${money(row.cut)} ${uiT("NIS")}`],
                [t("order.units"), `${money(row.units)} ${uiT("NIS")}`],
                [t("analysis.total"), `${money(row.total)} ${uiT("NIS")}`],
              ].map(([label, value], index) => <div key={index}><dt>{label}</dt><dd className="eb-pr-atomic" dir="ltr">{value}</dd></div>)}</dl>
            </article>)}
          </div> : (
          <table className="eb-pr-table" style={{ marginTop: 8 }}>
            <thead>
              <tr>
                <th>{t("diameter.col")}</th>
                <th>{t("analysis.weight")}</th>
                <th>{t("order.material", { tons: "" }).replace(/\(.*\)/, "").trim()}</th>
                <th>{t("order.bend")}</th>
                <th>{t("order.cut")}</th>
                <th>{t("order.units")}</th>
                <th>{t("analysis.total")}</th>
              </tr>
            </thead>
            <tbody>
              {byDiameter.map((r) => (
                <tr key={String(r.diameter)}>
                  <td>
                    <b>{r.diameter ?? "—"}</b>
                  </td>
                  <td>{r.weightTon.toFixed(3)}</td>
                  <td>{fmt0(r.material)}</td>
                  <td>{fmt0(r.bend)}</td>
                  <td>{fmt0(r.cut)}</td>
                  <td>{fmt0(r.units)}</td>
                  <td>
                    <b>{fmt0(r.total)}</b>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>)}
          <p className="eb-pr-sub">{t("analysis.note")}</p>
        </div>

        <div className="eb-pr-footer">
          <PBtn kind="secondary" onClick={onHide}>
            {t("common.close")}
          </PBtn>
          <PBtn kind="ghost" onClick={() => window.print()}>
            {t("analysis.print")}
          </PBtn>
        </div>
      </div>
    </MobileModalFrame>
</Modal>
  );
}
